import React, { useEffect } from 'react';
import CustomDialog from '../../../Components/Dialog/CustomDialog';
import { Grid } from '@mui/material';
import { useForm, FormProvider } from 'react-hook-form';
import { useIntl } from 'react-intl';
import FormCheckbox from '../../../Components/Form/FormCheckbox';
import FormSelect from '../../../Components/Form/FormSelect';
import FormTextField from '../../../Components/Form/FormTextField';

const BranchDialog = function BranchDialog({
  open,
  handleClose,
  onSubmit,
  branchData,
  title,
  dialogId,
}) {
  const intl = useIntl();

  const defaultValues = {
    branchCode: null,
    branchDescription: null,
    phoneNumber: null,
    emailAddress: null,
    address: null,
    address2: null,
    zipCode: null,
    city: null,
    state: null,
    country: null,
    timeZoneInfo: null,
    googleMapLink: null,
    linkedinLink: null,
    facebookLink: null,
    latitude: null,
    longitude: null,
    prefix3CX: null,
    mediaFolder: null,
    siM_ID: null,
    active: true,
    target: null,
  };

  const methods = useForm({
    mode: 'onChange',
    defaultValues,
  });

  const {
    handleSubmit: handleFormSubmit,
    formState: { errors, isValid },
    reset,
  } = methods;

  const fields = [
    {
      name: 'branchCode',
      label: intl.messages.BRANCH_CODE,
      type: 'number',
      required: true,
      visible: dialogId !== 'editBranch',
    },
    {
      name: 'branchDescription',
      label: intl.messages.BRANCHNAME,
      type: 'text',
      pattern: /^[A-Za-z\s]+$/,
      patternMessage: intl.messages.INVALID_BRANCHNAME,
      required: true,
    },
    {
      name: 'phoneNumber',
      label: intl.messages.PHONENUMBER,
      type: 'tel',
      pattern: /^[0-9]{10}$/,
      patternMessage: intl.messages.INVALID_PHONE_NUMBER,
    },
    {
      name: 'emailAddress',
      label: intl.messages.EMAILADDRESS,
      type: 'email',
      pattern:
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      patternMessage: intl.messages.INVALID_EMAIL_ADDRESS,
    },
    {
      name: 'address',
      label: intl.messages.ADDRESS,
      type: 'text',
      pattern: /^[A-Za-z0-9,.\s]+$/,
      patternMessage: intl.messages.INVALID_ADDRESS,
    },
    {
      name: 'address2',
      label: `${intl.messages.ADDRESS} 2`,
      type: 'text',
      pattern: /^[A-Za-z0-9,.\s]+$/,
      patternMessage: intl.messages.INVALID_ADDRESS,
    },
    {
      name: 'zipCode',
      label: intl.messages.ZIPCODE,
      type: 'text',
      pattern: /^[0-9]{3,10}$/,
      patternMessage: intl.messages.INVALID_ZIPCODE,
    },
    {
      name: 'city',
      label: intl.messages.CITY,
      type: 'text',
      pattern: /^[A-Za-z\s]+$/,
      patternMessage: intl.messages.INVALID_CITY,
    },
    {
      name: 'state',
      label: intl.messages.PROVINCE,
      patternMessage: intl.messages.INVALID_PROVINCE,
      type: 'text',
      pattern: /^[A-Za-z]{2}$/,
    },
    {
      name: 'country',
      label: intl.messages.COUNTRY,
      type: 'text',
      pattern: /^[A-Za-z\s]+$/,
      patternMessage: intl.messages.INVALID_COUNTRY,
    },
    {
      name: 'timeZoneInfo',
      label: intl.messages.TIME_ZONE_INFO,
      type: 'select',
      required: true,
      options: [
        { label: 'W. Europe Standard Time', value: 'W. Europe Standard Time' },
        {
          label: 'Central Europe Standard Time',
          value: 'Central Europe Standard Time',
        },
        { label: 'Pacific Standard Time', value: 'Pacific Standard Time' },
        { label: 'Eastern Standard Time', value: 'Eastern Standard Time' },
        { label: 'GMT Standard Time', value: 'GMT Standard Time' },
        { label: 'Romance Standard Time', value: 'Romance Standard Time' },
        {
          label: 'Central European Standard Time',
          value: 'Central European Standard Time',
        },
        {
          label: 'Arab Standard Time',
          value: 'Arab Standard Time',
        },
      ],
    },
    {
      name: 'googleMapLink',
      label: intl.messages.GOOGLEMAPLINK,
      type: 'url',
    },
    {
      name: 'linkedinLink',
      label: intl.messages.LINKEDINLINK,
      type: 'url',
    },
    {
      name: 'facebookLink',
      label: intl.messages.FACEBOOKLINK,
      type: 'url',
    },
    {
      name: 'latitude',
      label: intl.messages.LATITUDE,
      type: 'number',
      pattern: /^-?([1-8]?[0-9]|90)(\.[0-9]+)?$/,
      patternMessage: intl.messages.LATITUDE_INVALID,
    },
    {
      name: 'longitude',
      label: intl.messages.LONGITUDE,
      type: 'number',
      pattern: /^-?([1-9]?[0-9]|1[0-7][0-9]|180)(\.[0-9]+)?$/,
      patternMessage: intl.messages.LONGITUDE_INVALID,
    },
    {
      name: 'prefix3CX',
      label: intl.messages.PREFIX3CX,
      type: 'text',
      pattern: /^[0-9]{3}$/,
      patternMessage: intl.messages.THREECXPREFIX_INVALID,
    },
    { name: 'mediaFolder', label: intl.messages.MEDIA_FOLDER, type: 'text' },
    {
      name: 'siM_ID',
      label: intl.messages.SIM_ID,
      type: 'text',
      pattern: /^\+(\d{12})$/,
      patternMessage: intl.messages.INVALID_SIM_ID,
      required: true,
    },
    {
      name: 'target',
      label: intl.messages.CALLS_TARGET,
      type: 'number',
      pattern: /^\d+$/,
      patternMessage: intl.messages.CALLS_TARGET_INVALID,
    },
    {
      name: 'active',
      label: intl.messages.STATUS,
      type: 'checkbox',
    },
  ];

  useEffect(() => {
    if (open && dialogId === 'editBranch' && branchData) {
      const safeData = Object.fromEntries(
        Object.entries(branchData).map(([key, value]) => [key, value ?? null])
      );
      reset(safeData);
    }
  }, [open, dialogId, branchData, reset]);

  const handleSubmit = (data) => {
    const fixedData = {
      ...data,
      latitude: data.latitude || null,
      longitude: data.longitude || null,
      isDefault: data.isDefault === null ? false : data.isDefault,
    };
    onSubmit(fixedData, () => {
      reset(defaultValues);
    });
  };

  const renderField = ({
    name,
    label,
    type,
    required,
    pattern,
    patternMessage,
    options,
  }) => {
    if (type === 'checkbox') {
      return (
        <FormCheckbox name={name} error={errors[name]} required={required} />
      );
    }

    if (type === 'select') {
      return (
        <FormSelect
          name={name}
          label={label}
          required={required}
          error={errors[name]}
          options={options}
          noTranslation
        />
      );
    }

    const validateCallback = pattern
      ? (value) => {
          if (!value) return true;
          return pattern.test(value) || patternMessage;
        }
      : undefined;

    return (
      <FormTextField
        name={name}
        label={label}
        type={type}
        required={required}
        error={errors[name]}
        validateCallback={validateCallback}
      />
    );
  };

  return (
    <CustomDialog
      title={title}
      open={open}
      close={(_, reason) => {
        if (reason !== 'clickaway' && reason !== 'escapeKeyDown') {
          handleClose();
          reset(defaultValues);
        }
      }}
      disableEscapeKeyDown
      messageDialog
      maxWidth="md"
      fullWidth
      onSubmit={handleFormSubmit(handleSubmit)}
      submitDisabled={!isValid}
      noBackdrop
      noForm
    >
      <FormProvider {...methods}>
        <Grid container rowSpacing={1} columnSpacing={1}>
          {fields.map(
            (field) =>
              field.visible !== false && (
                <Grid size={{ md: 6, sm: 6, xs: 12 }} key={field.name}>
                  {renderField(field)}
                </Grid>
              )
          )}
        </Grid>
      </FormProvider>
    </CustomDialog>
  );
};

export default BranchDialog;
